import nacl from 'tweetnacl';
import * as util from 'tweetnacl-util';

/**
 * End-to-End Encryption Service
 * Uses TweetNaCl (NaCl) for secure message encryption
 * Keys are stored in browser localStorage and NEVER sent to server
 */

const STORAGE_KEY = 'broodbook_encryption_keypair';

export interface KeyPair {
  publicKey: string; // Base64 encoded
  secretKey: string; // Base64 encoded
}

/**
 * Get storage key for user-specific encryption keys
 */
function getStorageKey(userId?: string): string {
  return userId ? `broodbook_encryption_keypair_user_${userId}` : STORAGE_KEY;
}

/**
 * Generate a new encryption keypair for the user
 * Call this once during first login/signup
 */
export function generateKeyPair(): KeyPair {
  const keyPair = nacl.box.keyPair();
  return {
    publicKey: util.encodeBase64(keyPair.publicKey),
    secretKey: util.encodeBase64(keyPair.secretKey)
  };
}

/**
 * Store keypair in localStorage (per-user to support multiple users on same device)
 */
export function storeKeyPair(keyPair: KeyPair, userId?: string): void {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(keyPair));
}

/**
 * Retrieve keypair from localStorage (per-user)
 */
export function getKeyPair(userId?: string): KeyPair | null {
  const stored = localStorage.getItem(getStorageKey(userId));
  if (!stored) return null;
  return JSON.parse(stored);
}

/**
 * Check if user has encryption keys set up
 */
export function hasKeyPair(userId?: string): boolean {
  return localStorage.getItem(getStorageKey(userId)) !== null;
}

/**
 * Encrypt a message for a recipient
 * @param message - Plain text message
 * @param recipientPublicKey - Recipient's public key (base64)
 * @param senderSecretKey - Sender's secret key (base64)
 * @returns Encrypted message (base64) with nonce
 */
export function encryptMessage(
  message: string,
  recipientPublicKey: string,
  senderSecretKey: string
): string {
  try {
    const messageUint8 = util.decodeUTF8(message);
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const recipientPubKey = util.decodeBase64(recipientPublicKey);
    const senderSecKey = util.decodeBase64(senderSecretKey);

    const encrypted = nacl.box(
      messageUint8,
      nonce,
      recipientPubKey,
      senderSecKey
    );

    if (!encrypted) {
      throw new Error('Encryption failed');
    }

    // Combine nonce + encrypted message for transmission
    const fullMessage = new Uint8Array(nonce.length + encrypted.length);
    fullMessage.set(nonce);
    fullMessage.set(encrypted, nonce.length);

    return util.encodeBase64(fullMessage);
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt message');
  }
}

/**
 * Decrypt a message from a sender
 * @param encryptedMessage - Encrypted message with nonce (base64)
 * @param senderPublicKey - Sender's public key (base64)
 * @param recipientSecretKey - Recipient's secret key (base64)
 * @returns Decrypted plain text message
 */
export function decryptMessage(
  encryptedMessage: string,
  senderPublicKey: string,
  recipientSecretKey: string
): string {
  try {
    const messageWithNonce = util.decodeBase64(encryptedMessage);
    const nonce = messageWithNonce.slice(0, nacl.box.nonceLength);
    const message = messageWithNonce.slice(nacl.box.nonceLength);

    const senderPubKey = util.decodeBase64(senderPublicKey);
    const recipientSecKey = util.decodeBase64(recipientSecretKey);

    const decrypted = nacl.box.open(
      message,
      nonce,
      senderPubKey,
      recipientSecKey
    );

    if (!decrypted) {
      throw new Error('Decryption failed - message may be corrupted or keys are incorrect');
    }

    return util.encodeUTF8(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt message');
  }
}

/**
 * Initialize encryption for a new user
 * Generates and stores keypair, returns public key to share with server
 */
export function initializeEncryption(userId?: string): string {
  if (hasKeyPair(userId)) {
    const keyPair = getKeyPair(userId);
    return keyPair!.publicKey;
  }

  const keyPair = generateKeyPair();
  storeKeyPair(keyPair, userId);
  return keyPair.publicKey;
}

/**
 * Encrypt message for multiple recipient devices (iMessage-style)
 * @param message - Plain text message
 * @param recipientDevices - Array of { deviceId, publicKey }
 * @param senderSecretKey - Sender's secret key (base64)
 * @returns Array of { deviceId, encryptedContent }
 */
export function encryptMessageForDevices(
  message: string,
  recipientDevices: Array<{ deviceId: string; publicKey: string }>,
  senderSecretKey: string
): Array<{ deviceId: string; encryptedContent: string }> {
  const results: Array<{ deviceId: string; encryptedContent: string }> = [];

  for (const device of recipientDevices) {
    try {
      const encrypted = encryptMessage(message, device.publicKey, senderSecretKey);
      results.push({
        deviceId: device.deviceId,
        encryptedContent: encrypted
      });
    } catch (error) {
      console.error(`Failed to encrypt for device ${device.deviceId}:`, error);
      // Continue encrypting for other devices even if one fails
    }
  }

  return results;
}

/**
 * Clear encryption keys (logout)
 */
export function clearKeys(): void {
  localStorage.removeItem(STORAGE_KEY);
}
